import { Schema } from "effect";
import { MessageId } from "../../../ids/definitions";
import { MongoCustomerId, MongoUserId } from "../../../ids/mongo-ids";
import { MessageOrigin, MessageSenderType } from "./message";
import {
  ContactFormCustomField,
  ContactFormSource,
  InstagramDmKind,
  ViewedProduct,
  VoiceDirection,
  VoiceStatus,
} from "./message/channel-data";
import { MessageEmailAddressType } from "./message/email";
import { MessageRedoChatConfirmationState } from "./message/redo-chat";
import { MessageSmsService } from "./message/sms";

export const MessageContentType = Schema.Literal(
  "text",
  "toolConfirmationPrompt",
  "uiComponent",
);
export type MessageContentType = typeof MessageContentType.Type;

export const EmailAddressInput = Schema.Struct({
  addressType: MessageEmailAddressType,
  email: Schema.String.pipe(Schema.minLength(1)),
  name: Schema.optional(Schema.String),
});
export type EmailAddressInput = typeof EmailAddressInput.Type;

export const AttachmentInput = Schema.Struct({
  url: Schema.String,
  key: Schema.String,
  description: Schema.optional(Schema.String),
  mimeType: Schema.optional(Schema.String),
});
export type AttachmentInput = typeof AttachmentInput.Type;

export const AddInternalNoteInput = Schema.Struct({
  mentionedMongoUserIds: Schema.optional(Schema.Array(MongoUserId)),
  htmlContent: Schema.optional(Schema.String),
});
export type AddInternalNoteInput = typeof AddInternalNoteInput.Type;

const BaseMessageInputFields = {
  content: Schema.NullOr(Schema.String),
  contentType: MessageContentType,
  origin: MessageOrigin,
  sentAt: Schema.optional(Schema.DateFromSelf),
  externalMessageId: Schema.String,
  inReplyToId: Schema.optional(Schema.NullOr(MessageId)),
  inReplyToExternalId: Schema.optional(Schema.NullOr(Schema.String)),
  senderType: MessageSenderType,
  senderMongoCustomerId: Schema.optional(MongoCustomerId),
  senderMongoUserId: Schema.optional(MongoUserId),
  attachments: Schema.optional(Schema.Array(AttachmentInput)),
};

const GmailMessageInput = Schema.Struct({
  _tag: Schema.Literal("gmail"),
  ...BaseMessageInputFields,
  addresses: Schema.Array(EmailAddressInput),
  gmailMessageId: Schema.String,
  gmailHistoryId: Schema.NullOr(Schema.String),
  smtpMessageId: Schema.NullOr(Schema.String),
  headers: Schema.NullOr(Schema.Unknown),
  htmlBodyUrl: Schema.NullOr(Schema.String),
  parsedHtmlBody: Schema.NullOr(Schema.String),
});

const OutlookMessageInput = Schema.Struct({
  _tag: Schema.Literal("outlook"),
  ...BaseMessageInputFields,
  addresses: Schema.Array(EmailAddressInput),
  outlookMessageId: Schema.String,
  smtpMessageId: Schema.NullOr(Schema.String),
  headers: Schema.NullOr(Schema.Unknown),
  htmlBodyUrl: Schema.NullOr(Schema.String),
  parsedHtmlBody: Schema.NullOr(Schema.String),
});

const CustomEmailMessageInput = Schema.Struct({
  _tag: Schema.Literal("customEmail"),
  ...BaseMessageInputFields,
  addresses: Schema.Array(EmailAddressInput),
  customEmailMessageId: Schema.NullOr(Schema.String),
  smtpMessageId: Schema.NullOr(Schema.String),
  headers: Schema.NullOr(Schema.Unknown),
  htmlBodyUrl: Schema.NullOr(Schema.String),
  parsedHtmlBody: Schema.NullOr(Schema.String),
});

const SmsMessageInput = Schema.Struct({
  _tag: Schema.Literal("sms"),
  ...BaseMessageInputFields,
  // Wire transport for the send ("iMessage" vs "SMS"); drives blue-vs-green rendering. Optional so plain-SMS senders don't need to declare it — intake defaults to "SMS".
  messageService: Schema.optional(MessageSmsService),
  // Raw `postback_data` behind a tapped RCS chip; absent for typed replies.
  suggestionPostback: Schema.optional(Schema.NullOr(Schema.String)),
});

const MobileMessageInput = Schema.Struct({
  _tag: Schema.Literal("mobileMessage"),
  ...BaseMessageInputFields,
});

const RedoChatMessageInput = Schema.Struct({
  _tag: Schema.Literal("redoChat"),
  ...BaseMessageInputFields,
  redoChatContentType: Schema.optional(Schema.String),
  structuredContent: Schema.optional(Schema.NullOr(Schema.Unknown)),
  confirmationState: Schema.optional(
    Schema.NullOr(MessageRedoChatConfirmationState),
  ),
  pendingToolCallId: Schema.optional(Schema.NullOr(Schema.String)),
  viewedProduct: Schema.optional(Schema.NullOr(ViewedProduct)),
});

const VoiceMessageInput = Schema.Struct({
  _tag: Schema.Literal("voice"),
  ...BaseMessageInputFields,
  direction: VoiceDirection,
  status: VoiceStatus,
  telnyxCallControlId: Schema.String,
  telnyxRecordingId: Schema.optional(Schema.NullOr(Schema.String)),
  recordingUrl: Schema.optional(Schema.NullOr(Schema.String)),
  transcriptUrl: Schema.optional(Schema.NullOr(Schema.String)),
  durationSeconds: Schema.optional(Schema.NullOr(Schema.Number)),
  startedAt: Schema.DateFromSelf,
  connectedAt: Schema.optional(Schema.NullOr(Schema.DateFromSelf)),
  endedAt: Schema.optional(Schema.NullOr(Schema.DateFromSelf)),
});

const FacebookDmMessageInput = Schema.Struct({
  _tag: Schema.Literal("facebookDm"),
  ...BaseMessageInputFields,
  isEcho: Schema.Boolean,
  isDeleted: Schema.Boolean,
});

const InstagramDmMessageInput = Schema.Struct({
  _tag: Schema.Literal("instagramDm"),
  ...BaseMessageInputFields,
  isEcho: Schema.Boolean,
  isDeleted: Schema.Boolean,
  kind: Schema.optional(InstagramDmKind),
});

const FacebookCommentMessageInput = Schema.Struct({
  _tag: Schema.Literal("facebookComment"),
  ...BaseMessageInputFields,
  state: Schema.NullOr(Schema.Literal("normal", "deleted", "hidden", "edited")),
  attributes: Schema.NullOr(
    Schema.Array(Schema.Literal("has_been_replied_to", "liked")),
  ),
  privateRepliedCommentId: Schema.NullOr(Schema.String),
  privateRepliedCommentText: Schema.NullOr(Schema.String),
  privateRepliedCommentPermalink: Schema.NullOr(Schema.String),
});

const InstagramCommentMessageInput = Schema.Struct({
  _tag: Schema.Literal("instagramComment"),
  ...BaseMessageInputFields,
  state: Schema.NullOr(Schema.Literal("normal", "deleted", "hidden", "edited")),
  attributes: Schema.NullOr(
    Schema.Array(Schema.Literal("has_been_replied_to", "liked")),
  ),
  privateRepliedCommentId: Schema.NullOr(Schema.String),
  privateRepliedCommentText: Schema.NullOr(Schema.String),
  privateRepliedCommentPermalink: Schema.NullOr(Schema.String),
});

const AttentiveMessageInput = Schema.Struct({
  _tag: Schema.Literal("attentive"),
  ...BaseMessageInputFields,
});

const PostscriptMessageInput = Schema.Struct({
  _tag: Schema.Literal("postscript"),
  ...BaseMessageInputFields,
});

const TiktokMessageInput = Schema.Struct({
  _tag: Schema.Literal("tiktok"),
  ...BaseMessageInputFields,
});

const ContactFormMessageInput = Schema.Struct({
  _tag: Schema.Literal("contactForm"),
  ...BaseMessageInputFields,
  contactFormData: Schema.Struct({
    source: Schema.optionalWith(ContactFormSource, { default: () => "redo" }),
    name: Schema.NullOr(Schema.String),
    email: Schema.String,
    phone: Schema.NullOr(Schema.String),
    subject: Schema.NullOr(Schema.String),
    customFields: Schema.Array(ContactFormCustomField),
  }),
});

const InternalNoteMessageInput = Schema.Struct({
  _tag: Schema.Literal("internalNote"),
  ...BaseMessageInputFields,
  htmlContent: Schema.optional(Schema.String),
  mentionedMongoUserIds: Schema.optional(Schema.Array(MongoUserId)),
});

export const AddMessageWithChannelDataInput = Schema.Union(
  GmailMessageInput,
  OutlookMessageInput,
  CustomEmailMessageInput,
  SmsMessageInput,
  MobileMessageInput,
  RedoChatMessageInput,
  VoiceMessageInput,
  FacebookDmMessageInput,
  InstagramDmMessageInput,
  FacebookCommentMessageInput,
  InstagramCommentMessageInput,
  AttentiveMessageInput,
  PostscriptMessageInput,
  TiktokMessageInput,
  ContactFormMessageInput,
  InternalNoteMessageInput,
);
export type AddMessageWithChannelDataInput =
  typeof AddMessageWithChannelDataInput.Type;
